import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Plus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { slugify } from '@/lib/customPageApi';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface PageRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  updated_at: string;
}

export default function AdminCustomPages() {
  const navigate = useNavigate();
  const { push } = useToast();
  const [pages, setPages] = useState<PageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('rxk_custom_pages').select('id, slug, title, status, updated_at').order('updated_at', { ascending: false });
    setPages((data as PageRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createPage = async () => {
    setCreating(true);
    try {
      const base = slugify(`new-page-${Date.now().toString().slice(-6)}`);
      const { data, error } = await supabase
        .from('rxk_custom_pages')
        .insert({ title: 'New Page', slug: base, status: 'draft', blocks: [] })
        .select('id')
        .single();
      if (error) throw error;
      navigate(`/admin/pages/${data.id}`);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-bold text-ink">Custom Pages</h1>
          <Button size="sm" onClick={createPage} disabled={creating}>
            <Plus className="w-3.5 h-3.5" /> New Page
          </Button>
        </div>

        <Card>
          <CardHeader title="Pages" subtitle={`${pages.length} total`} icon={<Globe className="w-4 h-4" />} />
          <CardBody className="space-y-1">
            {loading ? (
              <Spinner />
            ) : pages.length === 0 ? (
              <EmptyState title="No custom pages yet" />
            ) : (
              pages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/admin/pages/${p.id}`)}
                  className="w-full flex items-center justify-between gap-3 px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150 text-left cursor-pointer"
                >
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">{p.title}</p>
                    <p className="text-[11px] text-ink-faint truncate">/{p.slug}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge tone={p.status === 'published' ? 'success' : 'neutral'}>{p.status}</Badge>
                    <p className="text-[10.5px] text-ink-disabled">{p.updated_at.slice(0, 10)}</p>
                  </div>
                </button>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
